import { cn } from '@lib/utils';
import { CheckIcon, PlusCircledIcon } from '@radix-ui/react-icons';
import { Badge } from '@registry/new-york/ui/badge';
import { Button } from '@registry/new-york/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@registry/new-york/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@registry/new-york/ui/popover';
import { Separator } from '@registry/new-york/ui/separator';
import { Column } from '@tanstack/react-table';
import React, { Dispatch, SetStateAction } from 'react';

import { TruncatedHeaderTitle } from '../TruncatedHeaderTitle/TruncatedHeaderTitle';

import styles from './DataTableFacetedFilter.module.scss';

interface DataTableFacetedFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: {
    label: React.ReactNode;
    value: any;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  onSelect?: (items: string[]) => void;
  values?: string[];
  fieldName?: string;
  list?: any[];
  splitBy?: string;
  className?: string;
  classNameContent?: string;
  store?: any;
  setLoader?: Dispatch<SetStateAction<boolean>>;
  hideSelectedBadges?: boolean;
  hasActiveFilters?: boolean;
  hideSearch?: boolean;
  /** Ellipsizes a long title; hovering reveals the full title over neighboring columns. */
  truncate?: boolean;
}

export const DataTableFacetedFilter = <TData, TValue>({
  column,
  title,
  options,
  onSelect,
  values,
  fieldName,
  list,
  splitBy,
  className,
  classNameContent,
  store,
  setLoader,
  hideSelectedBadges = false,
  hasActiveFilters = false,
  hideSearch = false,
  truncate = false,
}: DataTableFacetedFilterProps<TData, TValue>) => {
  const selectedValues = new Set(values || (column?.getFilterValue() as string[]));

  const numberOfUniqueValues = {};

  // Always prefer the list prop when provided — it is kept up-to-date by the parent
  // (re-fetched from the backend on every filter change), so it reflects the full
  // filtered dataset rather than just the current page.
  if (list?.length > 0) {
    list.forEach(item => {
      if (item.value !== undefined && item.count !== undefined) {
        numberOfUniqueValues[item.value] = item.count;
      } else {
        const key = fieldName ? item[fieldName] : item.value;
        numberOfUniqueValues[key] = (numberOfUniqueValues[key] || 0) + 1;
      }
    });
  } else if (column && typeof column.getFacetedRowModel === 'function') {
    // Fallback to client-side faceting only when no list is provided
    try {
      const facetedModel = column?.getFacetedRowModel();
      if (facetedModel && facetedModel?.rows) {
        facetedModel.rows.forEach(row => {
          const columnId = column.id || (column.columnDef as any).accessorKey;
          let value = row.getValue(columnId);

          if (value === undefined || value === null) return;
          if (splitBy) {
            value = (value as string).split(splitBy);
          }

          if (Array.isArray(value)) {
            (value as string[]).forEach(item => {
              const key = fieldName ? (item as any)[fieldName] : item;
              numberOfUniqueValues[key] = (numberOfUniqueValues[key] || 0) + 1;
            });
          } else {
            const key = fieldName ? value[fieldName] : value;
            numberOfUniqueValues[key] = (numberOfUniqueValues[key] || 0) + 1;
          }
        });
      }
    } catch (error) {
      console.error('Error accessing faceted row model:', error);
    }
  }

  if (!options) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(className, 'h-8 border-dashed bg-white', truncate && styles.buttonTruncate)}
          data-filter-chip={selectedValues?.size > 0 ? 'active' : 'idle'}
        >
          <PlusCircledIcon className="mr-2 h-4 w-4 flex-shrink-0" />
          <TruncatedHeaderTitle
            title={title ?? ''}
            truncate={truncate}
          />
          {selectedValues?.size > 0 && !hideSelectedBadges && (
            <>
              <Separator
                orientation="vertical"
                className="mx-2 h-4"
              />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
                data-filter-count
              >
                {selectedValues.size}
              </Badge>
              <div
                className="hidden space-x-1 lg:flex"
                data-filter-values
              >
                {selectedValues.size > 2 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  options
                    .filter(option => selectedValues.has(option.value))
                    .map(option => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        className="rounded-sm px-1 font-normal"
                      >
                        <div className="max-w-[130px] truncate ">{option.label}</div>
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
          {selectedValues?.size > 0 && hideSelectedBadges && (
            <>
              <Separator
                orientation="vertical"
                className="mx-2 h-4"
              />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal"
                data-filter-count
              >
                {selectedValues.size}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-[240px] max-w-none p-0', classNameContent)}
        align="start"
      >
        <Command>
          {!hideSearch && <CommandInput placeholder={title} />}
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options?.map(option => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (setLoader) {
                        setLoader(true);
                      }
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        selectedValues.add(option.value);
                      }
                      const filterValues = Array.from(selectedValues);
                      if (onSelect) {
                        onSelect(filterValues.length ? filterValues : undefined);
                      } else {
                        column?.setFilterValue(filterValues.length ? filterValues : undefined);
                      }
                      store?.setCurrentPage(1);
                    }}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className={cn('h-4 w-4')} />
                    </div>
                    {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                    <span
                      className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap"
                      style={{ scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent' }}
                    >
                      {option.label}
                    </span>
                    <span className="ml-2 flex-shrink-0 font-mono text-xs text-muted-foreground">
                      {numberOfUniqueValues[option.value] || 0}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      if (setLoader) {
                        setLoader(true);
                      }
                      if (onSelect) {
                        onSelect(undefined);
                      } else {
                        column?.setFilterValue(undefined);
                      }
                      store?.setCurrentPage(1);
                    }}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
